import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getPosition } from "@/lib/gps";
import { withBiometric } from "@/lib/webauthn";
import { Button } from "@/components/ui/button";
import { BookOpen, Play, Square, Loader2, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  a_l_heure: { label: "À l'heure", cls: "bg-green-100 text-green-700 border-green-200" },
  en_avance: { label: "En avance", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  en_retard: { label: "En retard", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  cours_valide: { label: "Cours validé", cls: "bg-green-100 text-green-700 border-green-200" },
  cours_valide_regularise: { label: "Régularisé", cls: "bg-teal-100 text-teal-700 border-teal-200" },
  regularise: { label: "Régularisé", cls: "bg-teal-100 text-teal-700 border-teal-200" },
  cours_non_termine: { label: "Non terminé", cls: "bg-red-100 text-red-700 border-red-200" },
};

const Badge = ({ k }) => {
  const b = STATUS_BADGE[k];
  if (!b) return null;
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>;
};

// Séances du jour de l'enseignant : Démarrer / Terminer le cours (pointage par séance, GPS obligatoire)
export const SeancesDuJour = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/timetable/my-sessions");
      setData(data);
    } catch { /* non-enseignant : ignorer */ }
  };
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);

  const startCourse = async (slot) => {
    setBusy(slot.id);
    try {
      const gps = await getPosition();
      await withBiometric(api, () => api.post("/timetable/sessions/start", { slot_id: slot.id, ...gps }));
      toast.success(`Cours démarré : ${slot.subject} — ${slot.class_name} ✓`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    finally { setBusy(""); }
  };

  const endCourse = async (slot) => {
    setBusy(slot.id);
    try {
      const gps = await getPosition();
      const { data: r } = await api.post(`/timetable/sessions/${slot.session.id}/end`, { ...gps });
      toast.success(`Cours terminé : ${Math.round(r.validated_minutes)} min validées`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="bg-white rounded-2xl border-2 border-[#1E3A8A]/15 shadow-sm overflow-hidden" data-testid="seances-du-jour">
      <div className="px-5 py-3 bg-[#1E3A8A] text-white flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading font-bold flex items-center gap-2"><BookOpen className="h-4 w-4" />Mes séances du jour — {data.day}</div>
        <div className="text-xs opacity-80 flex items-center gap-1"><MapPin className="h-3 w-3" />Pointage par séance géolocalisé</div>
      </div>
      <div className="p-4 space-y-3">
        {data.sessions.length === 0 && (
          <div className="text-center text-slate-400 py-6 text-sm" data-testid="no-sessions-today">Aucune séance prévue aujourd&apos;hui dans votre emploi du temps.</div>
        )}
        {data.sessions.map(s => {
          const sess = s.session;
          const open = sess && !sess.actual_end;
          const done = sess && sess.actual_end;
          return (
            <div key={s.id} className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${open ? "bg-green-50 border-green-300" : done ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200"}`} data-testid={`seance-${s.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-[#166534] text-sm"><Clock className="h-3 w-3 inline mr-1" />{s.start_time} – {s.end_time}</span>
                  <span className="font-semibold text-slate-800">{s.subject}</span>
                  <span className="text-xs text-slate-500">{s.class_name}{s.room ? ` · Salle ${s.room}` : ""}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  {sess && <Badge k={sess.status} />}
                  {done && <Badge k={sess.status_final} />}
                  {done && <span className="text-xs text-slate-500">{Math.round(sess.validated_minutes)} min validées / {sess.planned_minutes} min prévues</span>}
                  {open && <span className="text-xs text-green-700 font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />Cours en cours depuis {new Date(sess.actual_start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>}
                  {!sess && !s.can_start && <span className="text-xs text-slate-400">Pointage ouvert {data.rules.open_before_min} min avant le cours (délai max : {data.rules.max_start_delay_min} min)</span>}
                </div>
              </div>
              <div>
                {open ? (
                  <Button onClick={() => endCourse(s)} disabled={busy === s.id} size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1" data-testid={`btn-end-course-${s.id}`}>
                    {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Square className="h-3.5 w-3.5" />Terminer le cours</>}
                  </Button>
                ) : !sess && s.can_start ? (
                  <Button onClick={() => startCourse(s)} disabled={busy === s.id} size="sm" className="bg-[#166534] hover:bg-[#14532d] text-white gap-1" data-testid={`btn-start-course-${s.id}`}>
                    {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-3.5 w-3.5" />Démarrer le cours</>}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
